import { combineLatest } from 'rxjs';
import { switchMap, take, debounceTime } from 'rxjs/operators';
import {
  MultiPointInstallationListAction,
  MultiPointInstallationListActionTypes,
  MultiPointInstallationListState,
} from '../../types/multi-point-installation-list';
import {
  ResourceStatus,
  RESOURCE_IDLE,
  RESOURCE_LOADING,
} from '../../types/resource';
import { actions } from '../index';
import {
  serializeListViewState,
  updateQueryString,
} from '../../utils/rx-store-serialization';
import { getRequiredSessionToken } from '../../utils/session-token';

import createRxStore, { rxDispatch, ofType } from '..';
import { Filter, FilterOfValueType } from '../../types/filters';

import { Sort } from '../../types/sort';
import { Organization } from '../../types/api';

import { SenseiApi } from '../../utils/api';
import { Toaster } from '@densityco/ui';
import SessionStore from '../session';
import { SessionStatus } from '../../types/session';

import { fetchNextPageOfMPIs } from '../../rx-actions/load-mpis';

const PAGE_SIZE = 50;

export const initialState: MultiPointInstallationListState = {
  selectedMultiPointInstallationIds: [],

  sidePaneMultiPointInstallationId: null,

  filters: [],
  searchText: '',
  sort: { direction: 'none', column: null },

  page: 0,

  resource: RESOURCE_IDLE,
};

function reducer(
  state: MultiPointInstallationListState,
  action: MultiPointInstallationListAction,
): MultiPointInstallationListState {
  switch (action.type) {
    case MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_CHANGE_PAGE:
      return {
        ...state,

        sidePaneMultiPointInstallationId: null,

        filters: action.filters,
        searchText: action.searchText,
        sort: action.sort,

        resource: RESOURCE_IDLE,
      };

    case MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_SET_PAGE:
      if (state.resource.status !== ResourceStatus.COMPLETE) {
        return state;
      }
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            fetchingMoreMPIs: RESOURCE_LOADING,
          },
        },
      };

    case MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_LOADING:
      return {
        ...state,
        resource: RESOURCE_LOADING,
      };

    case MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_LOAD_COMPLETE:
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            items: action.items,
            total: action.total,
            m2m_capture_group_enabled: action.m2m_capture_group_enabled,
            fetchingMoreMPIs: RESOURCE_IDLE,
          },
        },
      };

    case MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_NEXT_PAGE_LOAD_ERROR:
      if (state.resource.status !== ResourceStatus.COMPLETE) {
        return state;
      }
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            fetchingMoreMPIs: {
              status: ResourceStatus.ERROR,
              error: action.error,
            },
          },
        },
      };

    case MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_NEXT_PAGE_LOAD_COMPLETE:
      if (state.resource.status !== ResourceStatus.COMPLETE) {
        return state;
      }
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            items: [...state.resource.data.items, ...action.mpis],
            fetchingMoreMPIs: RESOURCE_IDLE,
          },
        },
      };

    case MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_ERROR:
      return {
        ...state,
        resource: { status: ResourceStatus.ERROR, error: action.error },
      };

    case MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_SET_SEARCH_TEXT:
      return {
        ...state,
        searchText: action.text,
      };

    case MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_SET_FILTERS:
      return {
        ...state,
        filters: action.filters,
        selectedMultiPointInstallationIds: [],
      };

    case MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_SET_SORT:
      return {
        ...state,
        sort: { column: action.column, direction: action.direction },
      };

    case MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_CHANGE_CHECKS:
      return {
        ...state,
        selectedMultiPointInstallationIds: action.checked
          ? Array.from(
              new Set([
                ...state.selectedMultiPointInstallationIds,
                ...action.ids,
              ]),
            )
          : state.selectedMultiPointInstallationIds.filter(
              (id) => !action.ids.includes(id),
            ),
      };

    case MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_OPEN_SIDE_PANE:
      return {
        ...state,
        sidePaneMultiPointInstallationId: action.id,
      };

    case MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_CLOSE_SIDE_PANE:
      return { ...state, sidePaneMultiPointInstallationId: null };

    default:
      return state;
  }
}

const MultiPointInstallationListStore = createRxStore(
  'MultiPointInstallationListStore',
  initialState,
  reducer,
);

export function generateParams(
  filters: Array<Filter>,
  searchText: string,
  sort: Sort,
  organizationId: Organization['id'] | null,
): { [key: string]: string | number } {
  const filterToQuery = (
    definitionId: string,
    operatorId: string | undefined,
  ): string | undefined => {
    const filterFn =
      operatorId === undefined
        ? (f: FilterOfValueType<string>) => f.definitionId === definitionId
        : (f: FilterOfValueType<string>) =>
            f.definitionId === definitionId && f.operatorId === operatorId;
    const filter = filters.find(filterFn) as
      | FilterOfValueType<string>
      | undefined;
    if (filter) {
      return filter.value;
    } else {
      return undefined;
    }
  };

  let params = {
    organization_id: organizationId === null ? undefined : organizationId,
    search: searchText || undefined,
    number_of_sensors__lt: filterToQuery('number_of_sensors', 'lt'),
    number_of_sensors__lte: filterToQuery('number_of_sensors', 'lte'),
    number_of_sensors__gt: filterToQuery('number_of_sensors', 'gt'),
    number_of_sensors__gte: filterToQuery('number_of_sensors', 'gte'),
    // Note: backend stores accuracy as pct (ie 100%=100.) so don't need to convert
    total_dedupe_accuracy__lt: filterToQuery('total_accuracy', 'lt'),
    total_dedupe_accuracy__lte: filterToQuery('total_accuracy', 'lte'),
    total_dedupe_accuracy__gt: filterToQuery('total_accuracy', 'gt'),
    total_dedupe_accuracy__gte: filterToQuery('total_accuracy', 'gte'),
    capture_group_count__lt: filterToQuery('capture_group_count', 'lt'),
    capture_group_count__lte: filterToQuery('capture_group_count', 'lte'),
    capture_group_count__gt: filterToQuery('capture_group_count', 'gt'),
    capture_group_count__gte: filterToQuery('capture_group_count', 'gte'),
    fully_annotated_capture_group_count__lt: filterToQuery(
      'fully_annotated_capture_group_count',
      'lt',
    ),
    fully_annotated_capture_group_count__lte: filterToQuery(
      'fully_annotated_capture_group_count',
      'lte',
    ),
    fully_annotated_capture_group_count__gt: filterToQuery(
      'fully_annotated_capture_group_count',
      'gt',
    ),
    fully_annotated_capture_group_count__gte: filterToQuery(
      'fully_annotated_capture_group_count',
      'gte',
    ),

    page: 1,
    page_size: PAGE_SIZE,
  };

  if (sort.column !== null && sort.direction !== 'none') {
    // TODO: maybe make this consistent with the way ordering is done on the sensor and spances endpoints
    params['ordering'] = `${sort.direction === 'desc' ? '' : '-'}${
      sort.column
    }`;
    params['sort_direction'] = sort.direction;
  }
  return params;
}

async function loadMPIs(
  dispatch,
  jwtToken: string,
  filters: Array<Filter>,
  searchText: string,
  sort: Sort,
  organizationId: Organization['id'] | null,
) {
  dispatch({
    type: MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_LOADING,
  });

  const params = generateParams(filters, searchText, sort, organizationId);
  let mpis: any;
  let settings: any;
  try {
    mpis = await SenseiApi.multiPointInstallationList(jwtToken, params);
    try {
      settings = await SenseiApi.organizationSettings(jwtToken, organizationId);
    } catch (error) {
      console.log('Cannot get org settings');
    }
  } catch (error) {
    dispatch({
      type: MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_ERROR,
      error,
    });
    return;
  }

  dispatch({
    type: MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_LOAD_COMPLETE,
    items: mpis.results,
    total: mpis.total,
    m2m_capture_group_enabled: settings
      ? settings['m2m_capture_group_enabled']
      : false,
  });
}

// =================================
// SIDE EFFECT: When the filters / search update, update the query parameters
// =================================
actions
  .pipe(
    ofType(
      MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_SET_FILTERS,
      MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_SET_SORT,
      MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_SET_SEARCH_TEXT,
    ),
    debounceTime(500),
    switchMap(() => MultiPointInstallationListStore.pipe(take(1))),
  )
  .subscribe((state) => {
    const params = serializeListViewState(
      state.filters,
      state.searchText,
      state.sort,
    );
    updateQueryString(params);
  });

// =================================
// SIDE EFFECT: on initial load of the list view, figure out what request needs to be made and make it to
// load the data for the right view.
// =================================
actions
  .pipe(
    ofType<{
      organizationId: Organization['id'] | null;
      searchText: string;
      filters: Array<Filter>;
      sort: Sort;
    }>(
      MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_CHANGE_PAGE,
    ),
    switchMap(
      () => MultiPointInstallationListStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(({ action, state }) => {
    const jwtToken = getRequiredSessionToken();

    loadMPIs(
      rxDispatch,
      jwtToken,
      action.filters,
      action.searchText,
      action.sort,
      action.organizationId,
    );
  });

// =================================
// SIDE EFFECT: when a user reaches the end of the MPI list page, load more data
// =================================
actions
  .pipe(
    ofType(
      MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_SET_PAGE,
    ),
    switchMap(
      () =>
        combineLatest(
          MultiPointInstallationListStore.pipe(take(1)),
          SessionStore.pipe(take(1)),
        ),
      (action, [mpisState, sessionState]) => ({
        action,
        mpisState,
        sessionState,
      }),
    ),
  )
  .subscribe(({ action, mpisState, sessionState }) => {
    if (sessionState.status !== SessionStatus.LOGGED_IN) {
      return;
    }

    const a = action as any;

    fetchNextPageOfMPIs(
      rxDispatch,
      sessionState.token,
      mpisState.filters,
      mpisState.sort,
      mpisState.searchText,
      a.value,
      sessionState.selectedOrganizationId,
    );
  });

// =================================
// SIDE EFFECT: When search text changes, throttle the events but refetch the results
// from the server at most 1 second after typing has completed.
// =================================
actions
  .pipe(
    ofType<{ organizationId: Organization['id'] | null; text: string }>(
      MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_SET_SEARCH_TEXT,
    ),
    debounceTime(1000),
    switchMap(
      () => MultiPointInstallationListStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(({ action, state }) => {
    const jwtToken = getRequiredSessionToken();
    loadMPIs(
      rxDispatch,
      jwtToken,
      state.filters,
      action.text,
      state.sort,
      action.organizationId,
    );
  });

// =================================
// SIDE EFFECT: When sort or filter changes, refetch data from the server.
// =================================
actions
  .pipe(
    ofType(
      MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_SET_FILTERS,
      MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_SET_SORT,
    ),
    switchMap(
      () =>
        combineLatest([
          MultiPointInstallationListStore.pipe(take(1)),
          SessionStore.pipe(take(1)),
        ]),
      (action, [inConfigurationState, sessionState]) => ({
        inConfigurationState,
        sessionState,
      }),
    ),
  )
  .subscribe(({ inConfigurationState, sessionState }) => {
    if (sessionState.status !== SessionStatus.LOGGED_IN) {
      return;
    }
    loadMPIs(
      rxDispatch,
      sessionState.token,
      inConfigurationState.filters,
      inConfigurationState.searchText,
      inConfigurationState.sort,
      sessionState.selectedOrganizationId,
    );
  });

// =================================
// SIDE EFFECT: delete the MPI, request reload of MPIs
// =================================
actions
  .pipe(
    ofType<{
      id: string;
      organizationId: Organization['id'] | null;
    }>(
      MultiPointInstallationListActionTypes.MULTI_POINT_INSTALLATION_LIST_DELETE,
    ),
    switchMap(
      () => MultiPointInstallationListStore.pipe(take(1)),
      (action, state) => ({ action, state }),
    ),
  )
  .subscribe(({ action, state }) => {
    const jwtToken = getRequiredSessionToken();
    return SenseiApi.multiPointInstallationDelete(jwtToken, action.id)
      .then(() => {
        Toaster.showToast({ text: 'Deleted MPI.' });
        loadMPIs(
          rxDispatch,
          jwtToken,
          state.filters,
          state.searchText,
          state.sort,
          action.organizationId,
        );
      })
      .catch(() => {
        Toaster.showToast({ text: 'Error deleting MPI!', type: 'error' });
      });
  });

export default MultiPointInstallationListStore;
